import { getStoredMeta, loadStoredSession } from "@itaas/core/auth/auth.storage";
import { HOSPITOL_API_BASE_URL } from "@/core/config";
import type { Patient, PatientInput } from "@/core/patients/types";
import type { Admission, AdmissionChannel, AdmissionInput } from "@/core/admissions/types";
import type { Consultation, ConsultationInput } from "@/core/clinical/types";
import type { LabOrder, LabOrderInput, LabOrderStatus } from "@/core/laboratory/types";
import type { ImagingExam, ImagingExamInput, ImagingExamStatus } from "@/core/imaging/types";

async function hospitolFetch<T>(
  path: string,
  init?: RequestInit,
): Promise<T | null> {
  try {
    const headers: Record<string, string> = {
      "Content-Type": "application/json",
      Accept: "application/json",
      "X-Tenant-Slug": loadStoredSession()?.tenant?.slug ?? "demo-hospitol",
    };
    const { accessToken } = getStoredMeta();
    if (accessToken) headers.Authorization = `Bearer ${accessToken}`;

    const response = await fetch(`${HOSPITOL_API_BASE_URL}${path}`, {
      ...init,
      headers: { ...headers, ...(init?.headers as Record<string, string>) },
    });
    if (!response.ok) return null;
    return (await response.json()) as T;
  } catch {
    return null;
  }
}

export async function apiListPatients(query?: string): Promise<Patient[] | null> {
  const qs = query?.trim() ? `?q=${encodeURIComponent(query.trim())}` : "";
  return hospitolFetch<Patient[]>(`/patients${qs}`);
}

export async function apiGetPatient(id: string): Promise<Patient | null> {
  return hospitolFetch<Patient>(`/patients/${encodeURIComponent(id)}`);
}

export async function apiCreatePatient(input: PatientInput): Promise<Patient | null> {
  return hospitolFetch<Patient>("/patients", {
    method: "POST",
    body: JSON.stringify(input),
  });
}

export async function apiUpdatePatient(
  id: string,
  input: PatientInput,
): Promise<Patient | null> {
  return hospitolFetch<Patient>(`/patients/${encodeURIComponent(id)}`, {
    method: "PATCH",
    body: JSON.stringify(input),
  });
}

export async function apiDeletePatient(id: string): Promise<boolean> {
  const result = await hospitolFetch<{ ok: boolean }>(
    `/patients/${encodeURIComponent(id)}`,
    { method: "DELETE" },
  );
  return result?.ok === true;
}

export async function apiSyncPatient(patient: Patient): Promise<Patient | null> {
  return hospitolFetch<Patient>(`/patients/sync/${encodeURIComponent(patient.id)}`, {
    method: "PUT",
    body: JSON.stringify(patient),
  });
}

export async function apiListAdmissions(
  channel?: AdmissionChannel,
  arrivalMode?: import("@/core/admissions/types").EmergencyArrivalMode,
): Promise<Admission[] | null> {
  const params = new URLSearchParams();
  if (channel) params.set("channel", channel);
  if (arrivalMode) params.set("arrivalMode", arrivalMode);
  const qs = params.toString() ? `?${params.toString()}` : "";
  return hospitolFetch<Admission[]>(`/admissions${qs}`);
}

export async function apiGetAdmission(id: string): Promise<Admission | null> {
  return hospitolFetch<Admission>(`/admissions/${encodeURIComponent(id)}`);
}

export async function apiCreateAdmission(input: AdmissionInput): Promise<Admission | null> {
  return hospitolFetch<Admission>("/admissions", {
    method: "POST",
    body: JSON.stringify(input),
  });
}

export async function apiPatchAdmission(
  id: string,
  patch: Partial<Admission>,
): Promise<Admission | null> {
  return hospitolFetch<Admission>(`/admissions/${encodeURIComponent(id)}`, {
    method: "PATCH",
    body: JSON.stringify(patch),
  });
}

export async function apiSyncAdmission(admission: Admission): Promise<Admission | null> {
  return hospitolFetch<Admission>(`/admissions/sync/${encodeURIComponent(admission.id)}`, {
    method: "PUT",
    body: JSON.stringify(admission),
  });
}

export async function apiDeleteAdmission(id: string): Promise<boolean> {
  const result = await hospitolFetch<{ ok: boolean }>(
    `/admissions/${encodeURIComponent(id)}`,
    { method: "DELETE" },
  );
  return result?.ok === true;
}

export async function apiListConsultations(
  status?: Consultation["status"],
): Promise<Consultation[] | null> {
  const qs = status ? `?status=${encodeURIComponent(status)}` : "";
  return hospitolFetch<Consultation[]>(`/consultations${qs}`);
}

export async function apiGetConsultation(id: string): Promise<Consultation | null> {
  return hospitolFetch<Consultation>(`/consultations/${encodeURIComponent(id)}`);
}

export async function apiCreateConsultation(
  input: ConsultationInput,
): Promise<Consultation | null> {
  return hospitolFetch<Consultation>("/consultations", {
    method: "POST",
    body: JSON.stringify(input),
  });
}

export async function apiPatchConsultation(
  id: string,
  patch: Partial<Consultation>,
): Promise<Consultation | null> {
  return hospitolFetch<Consultation>(`/consultations/${encodeURIComponent(id)}`, {
    method: "PATCH",
    body: JSON.stringify(patch),
  });
}

export async function apiSyncConsultation(
  consultation: Consultation,
): Promise<Consultation | null> {
  return hospitolFetch<Consultation>(
    `/consultations/sync/${encodeURIComponent(consultation.id)}`,
    {
      method: "PUT",
      body: JSON.stringify(consultation),
    },
  );
}

export async function apiDeleteConsultation(id: string): Promise<boolean> {
  const result = await hospitolFetch<{ ok: boolean }>(
    `/consultations/${encodeURIComponent(id)}`,
    { method: "DELETE" },
  );
  return result?.ok === true;
}

export async function apiListBillingDocuments(
  status?: import("@/core/billing/billing-documents/types").BillingDocumentStatus,
  stayId?: string,
  surgeryCaseId?: string,
): Promise<import("@/core/billing/billing-documents/types").BillingDocument[] | null> {
  const params = new URLSearchParams();
  if (status) params.set("status", status);
  if (stayId) params.set("stayId", stayId);
  if (surgeryCaseId) params.set("surgeryCaseId", surgeryCaseId);
  const qs = params.toString() ? `?${params.toString()}` : "";
  return hospitolFetch(`/billing-documents${qs}`);
}

export async function apiSyncBillingDocument(
  document: import("@/core/billing/billing-documents/types").BillingDocument,
): Promise<import("@/core/billing/billing-documents/types").BillingDocument | null> {
  return hospitolFetch(`/billing-documents/sync/${encodeURIComponent(document.id)}`, {
    method: "PUT",
    body: JSON.stringify(document),
  });
}

export async function apiListLabOrders(status?: LabOrderStatus): Promise<LabOrder[] | null> {
  const qs = status ? `?status=${encodeURIComponent(status)}` : "";
  return hospitolFetch<LabOrder[]>(`/laboratory/orders${qs}`);
}

export async function apiGetLabOrder(id: string): Promise<LabOrder | null> {
  return hospitolFetch<LabOrder>(`/laboratory/orders/${encodeURIComponent(id)}`);
}

export async function apiCreateLabOrder(input: LabOrderInput): Promise<LabOrder | null> {
  return hospitolFetch<LabOrder>("/laboratory/orders", {
    method: "POST",
    body: JSON.stringify(input),
  });
}

export async function apiSyncLabOrder(order: LabOrder): Promise<LabOrder | null> {
  return hospitolFetch<LabOrder>(`/laboratory/orders/sync/${encodeURIComponent(order.id)}`, {
    method: "PUT",
    body: JSON.stringify(order),
  });
}

export async function apiListImagingExams(status?: ImagingExamStatus): Promise<ImagingExam[] | null> {
  const qs = status ? `?status=${encodeURIComponent(status)}` : "";
  return hospitolFetch<ImagingExam[]>(`/imaging/exams${qs}`);
}

export async function apiGetImagingExam(id: string): Promise<ImagingExam | null> {
  return hospitolFetch<ImagingExam>(`/imaging/exams/${encodeURIComponent(id)}`);
}

export async function apiCreateImagingExam(input: ImagingExamInput): Promise<ImagingExam | null> {
  return hospitolFetch<ImagingExam>("/imaging/exams", {
    method: "POST",
    body: JSON.stringify(input),
  });
}

export async function apiSyncImagingExam(exam: ImagingExam): Promise<ImagingExam | null> {
  return hospitolFetch<ImagingExam>(`/imaging/exams/sync/${encodeURIComponent(exam.id)}`, {
    method: "PUT",
    body: JSON.stringify(exam),
  });
}

export async function apiListHospitalWards(): Promise<
  import("@/core/hospitalization/types").HospitalWard[] | null
> {
  return hospitolFetch("/hospitalization/wards");
}

export async function apiSyncHospitalWard(
  ward: import("@/core/hospitalization/types").HospitalWard,
): Promise<import("@/core/hospitalization/types").HospitalWard | null> {
  return hospitolFetch(`/hospitalization/wards/sync/${encodeURIComponent(ward.id)}`, {
    method: "PUT",
    body: JSON.stringify(ward),
  });
}

export async function apiListHospitalRooms(
  wardId?: string,
): Promise<import("@/core/hospitalization/types").HospitalRoom[] | null> {
  const qs = wardId ? `?wardId=${encodeURIComponent(wardId)}` : "";
  return hospitolFetch(`/hospitalization/rooms${qs}`);
}

export async function apiSyncHospitalRoom(
  room: import("@/core/hospitalization/types").HospitalRoom,
): Promise<import("@/core/hospitalization/types").HospitalRoom | null> {
  return hospitolFetch(`/hospitalization/rooms/sync/${encodeURIComponent(room.id)}`, {
    method: "PUT",
    body: JSON.stringify(room),
  });
}

export async function apiListHospitalBeds(
  roomId?: string,
): Promise<import("@/core/hospitalization/types").HospitalBed[] | null> {
  const qs = roomId ? `?roomId=${encodeURIComponent(roomId)}` : "";
  return hospitolFetch(`/hospitalization/beds${qs}`);
}

export async function apiSyncHospitalBed(
  bed: import("@/core/hospitalization/types").HospitalBed,
): Promise<import("@/core/hospitalization/types").HospitalBed | null> {
  return hospitolFetch(`/hospitalization/beds/sync/${encodeURIComponent(bed.id)}`, {
    method: "PUT",
    body: JSON.stringify(bed),
  });
}

export async function apiListHospitalStays(
  status?: string,
): Promise<import("@/core/hospitalization/types").HospitalStay[] | null> {
  const qs = status ? `?status=${encodeURIComponent(status)}` : "";
  return hospitolFetch(`/hospitalization/stays${qs}`);
}

export async function apiCreateHospitalStay(input: {
  patientId: string;
  admissionId?: string | null;
  motif?: string;
  wardName?: string;
}): Promise<import("@/core/hospitalization/types").HospitalStay | null> {
  return hospitolFetch("/hospitalization/stays", {
    method: "POST",
    body: JSON.stringify(input),
  });
}

export async function apiAssignHospitalStayBed(
  stayId: string,
  bedId: string,
): Promise<import("@/core/hospitalization/types").HospitalStay | null> {
  return hospitolFetch(`/hospitalization/stays/${encodeURIComponent(stayId)}/assign-bed`, {
    method: "POST",
    body: JSON.stringify({ bedId }),
  });
}

export async function apiDischargeHospitalStay(
  stayId: string,
): Promise<import("@/core/hospitalization/types").HospitalStay | null> {
  return hospitolFetch(`/hospitalization/stays/${encodeURIComponent(stayId)}/discharge`, {
    method: "POST",
  });
}

export async function apiSyncHospitalStay(
  stay: import("@/core/hospitalization/types").HospitalStay,
): Promise<import("@/core/hospitalization/types").HospitalStay | null> {
  return hospitolFetch(`/hospitalization/stays/sync/${encodeURIComponent(stay.id)}`, {
    method: "PUT",
    body: JSON.stringify(stay),
  });
}

export async function apiListVitalSignRecords(opts?: {
  patientId?: string;
  stayId?: string;
  status?: string;
}): Promise<import("@/core/nursing/types").VitalSignRecord[] | null> {
  const params = new URLSearchParams();
  if (opts?.patientId) params.set("patientId", opts.patientId);
  if (opts?.stayId) params.set("stayId", opts.stayId);
  if (opts?.status) params.set("status", opts.status);
  const qs = params.toString() ? `?${params.toString()}` : "";
  return hospitolFetch(`/nursing/vitals${qs}`);
}

export async function apiValidateVitalSignRecord(
  id: string,
): Promise<import("@/core/nursing/types").VitalSignRecord | null> {
  return hospitolFetch(`/nursing/vitals/${encodeURIComponent(id)}/validate`, {
    method: "POST",
  });
}

export async function apiSyncVitalSignRecord(
  record: import("@/core/nursing/types").VitalSignRecord,
): Promise<import("@/core/nursing/types").VitalSignRecord | null> {
  return hospitolFetch(`/nursing/vitals/sync/${encodeURIComponent(record.id)}`, {
    method: "PUT",
    body: JSON.stringify(record),
  });
}

export async function apiListSurgeryCases(params?: {
  admissionId?: string;
  patientId?: string;
  status?: string;
}): Promise<import("@/core/surgery/types").SurgeryCase[] | null> {
  const q = new URLSearchParams();
  if (params?.admissionId) q.set("admissionId", params.admissionId);
  if (params?.patientId) q.set("patientId", params.patientId);
  if (params?.status) q.set("status", params.status);
  const qs = q.toString() ? `?${q}` : "";
  return hospitolFetch(`/surgery/cases${qs}`);
}

export async function apiCreateSurgeryCase(body: {
  admissionId: string;
  patientId?: string;
  stayId?: string | null;
  procedureName: string;
  surgeonName?: string;
  durationMinutes?: number;
  procedureFee?: number;
  operativeNotes?: string;
  scheduledAt?: string;
  consultationId?: string | null;
}): Promise<import("@/core/surgery/types").SurgeryCase | null> {
  return hospitolFetch("/surgery/cases", {
    method: "POST",
    body: JSON.stringify(body),
  });
}

export async function apiSyncSurgeryCase(
  surgeryCase: import("@/core/surgery/types").SurgeryCase,
): Promise<import("@/core/surgery/types").SurgeryCase | null> {
  return hospitolFetch(`/surgery/cases/sync/${encodeURIComponent(surgeryCase.id)}`, {
    method: "PUT",
    body: JSON.stringify(surgeryCase),
  });
}

export async function apiGetAdminSettings(): Promise<{
  id: string;
  tenantSlug: string;
  profile: import("@/core/admin/types").EstablishmentProfile;
  pharmacySettings: import("@/core/admin/types").PharmacySettings;
  receptionSettings: import("@/core/admin/types").ReceptionSettings;
  hospitalRevenueSettings: import("@/core/admin/types").HospitalRevenueSettings;
  updatedBy: string | null;
  createdAt: string;
  updatedAt: string;
} | null> {
  return hospitolFetch("/admin/settings");
}

export async function apiPutAdminSettings(body: {
  profile?: Partial<import("@/core/admin/types").EstablishmentProfile>;
  pharmacySettings?: Partial<import("@/core/admin/types").PharmacySettings>;
  receptionSettings?: Partial<import("@/core/admin/types").ReceptionSettings>;
  hospitalRevenueSettings?: Partial<import("@/core/admin/types").HospitalRevenueSettings>;
  updatedBy?: string | null;
}): Promise<{
  id: string;
  tenantSlug: string;
  profile: import("@/core/admin/types").EstablishmentProfile;
  pharmacySettings: import("@/core/admin/types").PharmacySettings;
  receptionSettings: import("@/core/admin/types").ReceptionSettings;
  hospitalRevenueSettings: import("@/core/admin/types").HospitalRevenueSettings;
  updatedBy: string | null;
  createdAt: string;
  updatedAt: string;
} | null> {
  return hospitolFetch("/admin/settings", {
    method: "PUT",
    body: JSON.stringify(body),
  });
}

export async function apiListMedicalUnits(): Promise<
  import("@/core/admin/types").MedicalUnit[] | null
> {
  return hospitolFetch("/admin/medical-units");
}

export async function apiCreateMedicalUnit(
  body: import("@/core/admin/types").MedicalUnitInput,
): Promise<import("@/core/admin/types").MedicalUnit | null> {
  return hospitolFetch("/admin/medical-units", {
    method: "POST",
    body: JSON.stringify(body),
  });
}

export async function apiSyncMedicalUnit(
  unit: import("@/core/admin/types").MedicalUnit,
): Promise<import("@/core/admin/types").MedicalUnit | null> {
  return hospitolFetch(`/admin/medical-units/sync/${encodeURIComponent(unit.id)}`, {
    method: "PUT",
    body: JSON.stringify(unit),
  });
}

export async function apiDeleteMedicalUnit(id: string): Promise<boolean> {
  const result = await hospitolFetch<{ ok: boolean }>(
    `/admin/medical-units/${encodeURIComponent(id)}`,
    { method: "DELETE" },
  );
  return result?.ok === true;
}

export async function apiListEstablishmentSites(): Promise<
  import("@/core/admin/types").EstablishmentSite[] | null
> {
  return hospitolFetch("/admin/sites");
}

export async function apiCreateEstablishmentSite(
  body: import("@/core/admin/types").EstablishmentSiteInput,
): Promise<import("@/core/admin/types").EstablishmentSite | null> {
  return hospitolFetch("/admin/sites", {
    method: "POST",
    body: JSON.stringify(body),
  });
}

export async function apiSyncEstablishmentSite(
  site: import("@/core/admin/types").EstablishmentSite,
): Promise<import("@/core/admin/types").EstablishmentSite | null> {
  return hospitolFetch(`/admin/sites/sync/${encodeURIComponent(site.id)}`, {
    method: "PUT",
    body: JSON.stringify(site),
  });
}

export async function apiDeleteEstablishmentSite(id: string): Promise<boolean> {
  const result = await hospitolFetch<{ ok: boolean }>(
    `/admin/sites/${encodeURIComponent(id)}`,
    { method: "DELETE" },
  );
  return result?.ok === true;
}

export type DirectionSummary = {
  tenantSlug: string;
  generatedAt: string;
  establishment: { configured: boolean; name: string };
  medicalUnits: { total: number; active: number };
  sites: { total: number; active: number };
  domains: {
    hospitalStays: number;
    surgeryCases: number;
    billingDocuments: number;
    pharmacyInvoices: number;
  };
};

export async function apiGetDirectionSummary(): Promise<DirectionSummary | null> {
  return hospitolFetch("/admin/direction/summary");
}

export async function apiListPrescriptions(): Promise<import("@/core/clinical/types").Prescription[] | null> {
  return hospitolFetch("/prescriptions");
}

export async function apiSyncPrescription(
  prescription: import("@/core/clinical/types").Prescription,
): Promise<import("@/core/clinical/types").Prescription | null> {
  return hospitolFetch(`/prescriptions/sync/${encodeURIComponent(prescription.id)}`, {
    method: "PUT",
    body: JSON.stringify(prescription),
  });
}

export async function apiListPharmacyInvoices(): Promise<
  import("@/core/pharmacy/types").ValidatedInvoice[] | null
> {
  return hospitolFetch("/pharmacy/invoices");
}

export async function apiSyncPharmacyInvoice(
  invoice: import("@/core/pharmacy/types").ValidatedInvoice,
): Promise<import("@/core/pharmacy/types").ValidatedInvoice | null> {
  return hospitolFetch(`/pharmacy/invoices/sync/${encodeURIComponent(invoice.id)}`, {
    method: "PUT",
    body: JSON.stringify(invoice),
  });
}

export async function notifyPrescriptionReady(input: {
  prescriptionId: string;
  patientName: string;
  recipientId?: string;
  recipientEmail?: string;
  tenantId?: string;
}): Promise<boolean> {
  const result = await hospitolFetch<{ ok: boolean }>("/pharmacy/prescriptions/ready", {
    method: "POST",
    body: JSON.stringify({ ...input, tenantId: input.tenantId }),
  });
  return result?.ok === true;
}

export async function notifyLowStockAlerts(input: {
  alerts: Array<{ productId: string; label: string; available: number; threshold: number }>;
  recipientId?: string;
  tenantId?: string;
}): Promise<boolean> {
  const result = await hospitolFetch<{ ok: boolean }>("/pharmacy/low-stock-alerts", {
    method: "POST",
    body: JSON.stringify({ ...input, tenantId: input.tenantId }),
  });
  return result?.ok === true;
}

export async function notifyEmergencyAdmission(input: {
  admissionId: string;
  patientName: string;
  triageLevel: "red" | "yellow" | "green";
  motif: string;
  recipientId?: string;
  tenantId?: string;
}): Promise<boolean> {
  const result = await hospitolFetch<{ ok: boolean }>("/hospitol/events/emergency-admission", {
    method: "POST",
    body: JSON.stringify({ ...input, tenantId: input.tenantId }),
  });
  return result?.ok === true;
}

export async function notifyImagingReportReady(input: {
  examId: string;
  examNumber: string;
  examType: string;
  patientName: string;
  recipientId?: string;
  tenantId?: string;
}): Promise<boolean> {
  const result = await hospitolFetch<{ ok: boolean }>("/hospitol/events/imaging-report-ready", {
    method: "POST",
    body: JSON.stringify({ ...input, tenantId: input.tenantId }),
  });
  return result?.ok === true;
}

export async function notifyAppointmentReminder(input: {
  appointmentId: string;
  patientId: string;
  patientName: string;
  appointmentAt: string;
  recipientPhone?: string;
  serviceUnit?: string;
  practitioner?: string;
  motif?: string;
  tenantId?: string;
}): Promise<boolean> {
  const result = await hospitolFetch<{ ok: boolean }>("/hospitol/events/appointment-reminder", {
    method: "POST",
    body: JSON.stringify({ ...input, tenantId: input.tenantId }),
  });
  return result?.ok === true;
}
